import React from "react";
import { captureException } from "../lib/sentry";

interface Props {
  children: React.ReactNode;
  // Optional fallback override. Defaults to the generic "something went
  // wrong" UI.
  fallback?: React.ReactNode;
  // Optional label — when provided, the boundary name is tagged into the
  // Sentry event so we can see which boundary tripped (e.g. "map",
  // "root", "modal").
  boundary?: string;
}

interface State {
  hasError: boolean;
  error?: Error;
}

// ErrorBoundary catches render + lifecycle errors in its subtree and
// shows a fallback UI instead of white-screening the whole app. Events
// are also reported to Sentry if the SDK is initialised. Effects (async
// code, event handlers) still bypass this — those are caught by the
// global Sentry handler in index.tsx.
export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary] caught", this.props.boundary || "root", error, info);
    captureException(error, {
      tags: this.props.boundary ? { errorBoundary: this.props.boundary } : undefined,
      contexts: { componentStack: { stack: info.componentStack } },
    });
  }

  reset = () => {
    this.setState({ hasError: false, error: undefined });
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    if (this.props.fallback) return this.props.fallback;
    return (
      <div
        style={{
          padding: 24,
          margin: 24,
          background: "var(--bg-card, #1a1b23)",
          border: "1px solid var(--border, #2d2f3a)",
          borderRadius: 12,
          color: "var(--text-primary, #e5e7eb)",
          maxWidth: 520,
          fontFamily: "var(--font-body, system-ui)",
        }}
        role="alert"
      >
        <h2 style={{ marginTop: 0 }}>Something went wrong</h2>
        <p style={{ color: "var(--text-muted, #94a3b8)" }}>
          The app hit an unexpected error. The team has been notified. You can
          try reloading — or keep using the parts that still work.
        </p>
        <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
          <button
            onClick={this.reset}
            style={{
              padding: "8px 14px",
              borderRadius: 8,
              border: "1px solid var(--border)",
              background: "var(--bg-surface, #252732)",
              color: "var(--text-primary)",
              cursor: "pointer",
            }}
          >
            Try again
          </button>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: "8px 14px",
              borderRadius: 8,
              border: "none",
              background: "var(--accent-blue, #3b82f6)",
              color: "white",
              cursor: "pointer",
            }}
          >
            Reload page
          </button>
        </div>
        {process.env.NODE_ENV !== "production" && this.state.error && (
          <pre
            style={{
              marginTop: 16,
              padding: 12,
              background: "rgba(0,0,0,0.3)",
              borderRadius: 6,
              fontSize: 12,
              overflow: "auto",
              color: "var(--accent-red, #ef4444)",
            }}
          >
            {this.state.error.message}
            {"\n"}
            {this.state.error.stack}
          </pre>
        )}
      </div>
    );
  }
}

export default ErrorBoundary;
